import { cn } from '@/lib/utils';
import {
  Activity,
  BarChart3,
  Building2,
  ChevronLeft,
  Database,
  Globe,
  Loader2,
  Mail,
  MessageSquare,
  ScrollText,
  ShieldCheck,
  Smartphone,
  UserPlus,
  Users
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AdminMe,
  AdminUser,
  AuditEntry,
  CustomerDetail,
  CustomerRow,
  DataOverview,
  MemoryProposal,
  MemoryRow,
  ReportRow,
  Usage,
  UsageUserRow,
  VisibilityOverview,
  apiAdminAudit,
  apiAdminCustomer,
  apiAdminCustomers,
  apiAdminData,
  apiAdminDeleteEntitlement,
  apiAdminDeleteMemory,
  apiAdminMe,
  apiAdminMemory,
  apiAdminPatchCustomer,
  apiAdminPatchUser,
  apiAdminReviewMemory,
  apiAdminReports,
  apiAdminSetDescription,
  apiAdminSetEntitlement,
  apiAdminSetFlag,
  apiAdminUsage,
  apiAdminUsageOverview,
  apiAdminSetTableAccessMode,
  apiAdminSetChatAvailability,
  apiAdminSetVisibility,
  apiAdminUsers,
  apiAdminVisibility,
  ENGLISH_ONLY,
  describeSchedule,
  featureHelp,
  featureLabel,
  formatDateTime,
  reportEmailUrl,
  timeAgo
} from '@/components/admin/api';
import ConversationBrowser, {
  OpenThreadRef
} from '@/components/admin/ConversationBrowser';
import SpendOverview from '@/components/admin/SpendOverview';
import UsageOverview from '@/components/admin/UsageOverview';
import {
  CreateUserDialog,
  WelcomeEmailDialog
} from '@/components/admin/CreateUser';
import { describeAudit } from '@/components/admin/describeAudit';
import { Header } from '@/components/header';
import { Badge } from '@/components/ui/badge';
import PlatformSettings from '@/components/admin/PlatformSettings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

// Configurations — the in-platform replacement for the ERP-side PHP pages.
// Every permission is enforced by the API; this page only reflects what the
// caller's role allows, and shows the reason whenever the server refuses.

type Tab =
  | 'features'
  | 'users'
  | 'data'
  | 'conversations'
  | 'reports'
  | 'audit';

const TAB_IDS: Tab[] = [
  'features',
  'users',
  'data',
  'conversations',
  'reports',
  'audit'
];

// Above any one company: every company, everyone's usage, our spend, and a
// search across every conversation.
type View = 'companies' | 'usage' | 'spend' | 'conversations';

const VIEWS: { id: View; label: string; icon: typeof Users }[] = [
  { id: 'companies', label: 'Companies', icon: Building2 },
  { id: 'usage', label: 'Usage', icon: Activity },
  { id: 'spend', label: 'Spend & volume', icon: BarChart3 },
  { id: 'conversations', label: 'Conversations', icon: MessageSquare }
];

const TABS: { id: Tab; label: string; icon: typeof Users }[] = [
  { id: 'features', label: 'Features & plan', icon: ShieldCheck },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'data', label: 'Data', icon: Database },
  { id: 'conversations', label: 'Conversations', icon: MessageSquare },
  { id: 'reports', label: 'Reports', icon: Mail },
  { id: 'audit', label: 'Audit log', icon: ScrollText }
];

export default function Admin() {
  const [me, setMe] = useState<AdminMe | null>(null);
  const [denied, setDenied] = useState<string | null>(null);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [reports, setReports] = useState<{
    items: ReportRow[];
    attention: number;
  } | null>(null);
  const [emailPreview, setEmailPreview] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [data, setData] = useState<DataOverview | null>(null);
  const [memory, setMemory] = useState<MemoryRow[] | null>(null);
  const [proposals, setProposals] = useState<MemoryProposal[]>([]);
  // Which topic's chat-availability checkbox is mid-save, so it cannot be
  // clicked twice while the catalogue bump is still in flight.
  const [savingTopic, setSavingTopic] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<VisibilityOverview | null>(
    null
  );
  // The page address carries where the admin is (view, company, tab, an open
  // conversation and its search), so a link can be shared and Back works.
  const [params, setParams] = useSearchParams();
  const view: View =
    (VIEWS.find((v) => v.id === params.get('view'))?.id as View) ??
    'companies';
  const tabParam = params.get('tab') as Tab | null;
  const tab: Tab = tabParam && TAB_IDS.includes(tabParam) ? tabParam : 'features';
  const threadParam = params.get('thread');
  const customerParam = params.get('customer');
  const urlThread: OpenThreadRef | null =
    threadParam && customerParam
      ? { id: threadParam, customer_pk: Number(customerParam) }
      : null;
  const urlQuery = params.get('q') ?? '';
  const setTab = (t: Tab) =>
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.set('tab', t);
      n.delete('thread');
      n.delete('q');
      return n;
    });
  const setView = (v: View) =>
    setParams(v === 'companies' ? {} : { view: v });
  const [overview, setOverview] = useState<UsageUserRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'err';
    text: string;
  } | null>(null);
  const [promptDraft, setPromptDraft] = useState('');
  // Collapsed by default: this is not what an admin comes here for most
  // days, and the customer list is.
  const [showPlatform, setShowPlatform] = useState(false);
  const [creating, setCreating] = useState(false);
  const [welcomeFor, setWelcomeFor] = useState<AdminUser | null>(null);
  const [lastCompany, setLastCompany] = useState<string | null>(null);

  useEffect(() => {
    apiAdminMe()
      .then(setMe)
      .catch((e) => setDenied(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!me) return;
    apiAdminCustomers()
      .then((r) => setCustomers(r.items))
      .catch((e) => setNotice({ kind: 'err', text: e.message }));
  }, [me]);

  const loadCustomer = useCallback((pk: number) => {
    setBusy(true);
    setNotice(null);
    Promise.all([
      apiAdminCustomer(pk),
      apiAdminUsers(pk),
      apiAdminUsage(pk).catch(() => null),
      // Last seen and questions per user, for the Users tab.
      apiAdminUsageOverview(30, pk).catch(() => null),
      // With the customer, not on opening the tab: the tab shows how many
      // reports need someone before anyone thinks to look.
      apiAdminReports(pk).catch(() => null)
    ])
      .then(([d, u, usg, ov, rep]) => {
        setDetail(d);
        setOverview(ov?.items ?? null);
        setUsers(u.items);
        setLastCompany(u.last_company ?? null);
        setUsage(usg);
        setReports(rep);
        setPromptDraft(d.customer.additional_prompt ?? '');
      })
      .catch((e) => setNotice({ kind: 'err', text: e.message }))
      .finally(() => setBusy(false));
  }, []);

  const resetCustomer = () => {
    setAudit([]);
    setData(null);
    // The memory card belongs to the customer too: kept, it showed the last
    // customer's facts and suggestions under this one's name.
    setMemory(null);
    setProposals([]);
    setVisibility(null);
    setReports(null);
    setEmailPreview(null);
    setOverview(null);
  };

  const openCustomer = (pk: number, toTab: Tab = 'features') =>
    setParams({ customer: String(pk), tab: toTab });

  // The address decides which company is open.
  useEffect(() => {
    if (!me) return;
    const pk =
      view === 'companies' && customerParam ? Number(customerParam) : null;
    if (pk === selected) return;
    setSelected(pk);
    resetCustomer();
    if (pk !== null) loadCustomer(pk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, view, customerParam]);

  // Activity per company for the company cards.
  const [companyStats, setCompanyStats] = useState<Map<
    number,
    { active7: number; questions: number; lastSeen: string | null }
  > | null>(null);
  useEffect(() => {
    if (!me || selected !== null || view !== 'companies' || companyStats)
      return;
    apiAdminUsageOverview(30)
      .then((ov) => {
        const m = new Map<
          number,
          { active7: number; questions: number; lastSeen: string | null }
        >();
        const week = Date.now() - 7 * 86400000;
        ov.items
          .filter((u) => u.role === 'USER')
          .forEach((u) => {
            const s = m.get(u.customer_pk) ?? {
              active7: 0,
              questions: 0,
              lastSeen: null
            };
            s.questions += u.questions;
            if (u.last_seen_at && Date.parse(u.last_seen_at) >= week)
              s.active7 += 1;
            if (
              u.last_seen_at &&
              (!s.lastSeen || Date.parse(u.last_seen_at) > Date.parse(s.lastSeen))
            )
              s.lastSeen = u.last_seen_at;
            m.set(u.customer_pk, s);
          });
        setCompanyStats(m);
      })
      .catch(() => undefined);
  }, [me, selected, view, companyStats]);

  const seenBy = useMemo(() => {
    const m = new Map<string, UsageUserRow>();
    (overview ?? []).forEach((u) => m.set(u.id, u));
    return m;
  }, [overview]);

  useEffect(() => {
    if (selected === null) return;
    if (tab === 'data' && data === null) {
      apiAdminData(selected)
        .then(setData)
        .catch((e) => setNotice({ kind: 'err', text: e.message }));
    }
    if (tab === 'data' && memory === null) {
      apiAdminMemory(selected)
        .then((r) => {
          setMemory(r.items);
          setProposals(r.proposals ?? []);
        })
        .catch(() => setMemory([]));
    }
    if (tab === 'data' && visibility === null) {
      apiAdminVisibility(selected)
        .then(setVisibility)
        .catch((e) => setNotice({ kind: 'err', text: e.message }));
    }
    if (tab === 'audit' && audit.length === 0) {
      apiAdminAudit(selected)
        .then((r) => setAudit(r.items))
        .catch((e) => setNotice({ kind: 'err', text: e.message }));
    }
  }, [tab, selected, audit.length, data, memory, visibility]);

  // An open conversation and its search go into the address.
  const syncThread = useCallback(
    (s: { q: string; thread: OpenThreadRef | null }) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          if (s.thread) {
            n.set('thread', s.thread.id);
            if (view === 'conversations')
              n.set('customer', String(s.thread.customer_pk));
          } else {
            n.delete('thread');
            if (view === 'conversations') n.delete('customer');
          }
          if (s.q.trim()) n.set('q', s.q.trim());
          else n.delete('q');
          return n;
        },
        { replace: true }
      ),
    [setParams, view]
  );
  const showError = useCallback(
    (text: string) => setNotice({ kind: 'err', text }),
    []
  );

  const afterCreate = (pk: number) => {
    apiAdminCustomers()
      .then((r) => setCustomers(r.items))
      .catch(() => undefined);
    if (selected === pk) {
      loadCustomer(pk);
      setTab('users');
    } else {
      openCustomer(pk, 'users');
    }
  };

  const tenantFlags = useMemo(() => {
    const map = new Map<string, boolean>();
    detail?.flags
      .filter((f) => f.user_id === null)
      .forEach((f) => map.set(f.feature, f.enabled));
    return map;
  }, [detail]);

  const entitlementFor = useCallback(
    (feature: string) =>
      detail?.entitlements.find((e) => e.feature === feature) ?? null,
    [detail]
  );

  const seatsUsed = useCallback(
    (feature: string) =>
      detail?.flags.filter(
        (f) => f.user_id !== null && f.enabled && f.feature === feature
      ).length ?? 0,
    [detail]
  );

  const act = async (fn: () => Promise<unknown>, okText: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      setNotice({ kind: 'ok', text: okText });
      if (selected !== null) {
        loadCustomer(selected);
        if (tab === 'data')
          apiAdminData(selected)
            .then(setData)
            .catch(() => undefined);
        if (tab === 'data')
          apiAdminMemory(selected)
            .then((r) => {
          setMemory(r.items);
          setProposals(r.proposals ?? []);
        })
            .catch(() => undefined);
      }
    } catch (e: any) {
      // The API explains WHY (not entitled, seats exhausted, above ceiling) —
      // show that verbatim rather than a generic failure.
      setNotice({ kind: 'err', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        <Loader2 className="size-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (denied || !me) {
    return (
      <SidebarProvider>
        <div className="flex flex-col w-full h-screen">
          <Header />
          <div className="flex flex-1 items-center justify-center p-6">
            <Card className="max-w-md p-6 text-center">
              <ShieldCheck className="size-8 mx-auto mb-3 text-muted-foreground" />
              <div className="font-semibold mb-1">Configurations</div>
              <p className="text-sm text-muted-foreground">
                {denied ?? 'Admin access is not enabled for this account.'}
              </p>
            </Card>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  const current = customers.find((c) => c.pk === selected);

  return (
    <SidebarProvider>
      <div className="flex flex-col w-full h-screen overflow-hidden">
        <Header />
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto max-w-6xl flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  <ShieldCheck className="size-5" /> Configurations
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {me.role === 'SUPER_ADMIN'
                    ? 'BuduCloud administration — every customer.'
                    : `Partner administration — ${me.scope?.length ?? 0} customers in your scope.`}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  onClick={() => setCreating(true)}
                  disabled={customers.length === 0}
                >
                  <UserPlus className="size-4 mr-1" /> New user
                </Button>
                <Badge variant="outline" className="text-xs">
                  {me.email} ·{' '}
                  {me.role === 'SUPER_ADMIN' ? 'Super admin' : 'Partner admin'}
                </Badge>
              </div>
            </div>

            <CreateUserDialog
              open={creating}
              onOpenChange={setCreating}
              customers={customers}
              initialCustomerPk={selected}
              onCreated={afterCreate}
            />
            <WelcomeEmailDialog
              user={welcomeFor}
              defaultCompany={lastCompany}
              onClose={() => setWelcomeFor(null)}
              onSent={() => selected !== null && loadCustomer(selected)}
            />

            {notice && (
              <div
                className={`rounded-lg border px-3 py-2 text-sm ${
                  notice.kind === 'ok'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                    : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200'
                }`}
              >
                {notice.text}
              </div>
            )}

            {selected === null && (
              <div className="flex gap-1 border-b overflow-x-auto">
                {VIEWS.map((v) => {
                  const Icon = v.icon;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setView(v.id)}
                      className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap ${
                        view === v.id
                          ? 'border-primary text-foreground font-medium'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon className="size-4" />
                      {v.label}
                    </button>
                  );
                })}
              </div>
            )}

            {selected === null && view === 'usage' && (
              <UsageOverview
                customers={customers}
                sees_cost={me.sees_cost}
                onOpenThread={(t) =>
                  setParams({
                    view: 'conversations',
                    customer: String(t.customer_pk),
                    thread: t.id
                  })
                }
                onOpenCustomer={(pk) => openCustomer(pk, 'users')}
                onError={showError}
              />
            )}

            {selected === null && view === 'spend' && (
              <SpendOverview sees_cost={me.sees_cost} onError={showError} />
            )}

            {selected === null && view === 'conversations' && (
              <ConversationBrowser
                key="all"
                customerPk={null}
                customers={customers}
                initialQuery={urlQuery}
                initialThread={urlThread}
                onStateChange={syncThread}
                onError={showError}
              />
            )}

            {me.can_manage_platform && selected === null && view === 'companies' && (
              <Card className="p-4">
                <button
                  className="w-full flex items-center justify-between gap-2 text-left"
                  onClick={() => setShowPlatform((v) => !v)}
                >
                  <span>
                    <span className="font-medium">Platform</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      the sandbox pool every customer shares
                    </span>
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {showPlatform ? 'Hide' : 'Show'}
                  </span>
                </button>
                {showPlatform && (
                  <div className="mt-4">
                    <PlatformSettings />
                  </div>
                )}
              </Card>
            )}

            {selected === null ? (
              view === 'companies' && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {customers.map((c) => {
                  const st = companyStats?.get(c.pk);
                  return (
                  <Card
                    key={c.pk}
                    className="p-4 cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => openCustomer(c.pk)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 font-semibold">
                        <Building2 className="size-4 text-muted-foreground" />
                        {c.name}
                      </div>
                      {st && (
                        <span
                          className="text-xs text-muted-foreground"
                          title={formatDateTime(st.lastSeen)}
                        >
                          {st.lastSeen ? timeAgo(st.lastSeen) : 'no activity'}
                        </span>
                      )}
                    </div>
                    {st && (
                      <div className="text-sm mt-2 flex flex-wrap gap-x-4">
                        <span>
                          <strong>{st.questions}</strong>{' '}
                          <span className="text-muted-foreground">
                            questions / 30d
                          </span>
                        </span>
                        <span>
                          <strong>{st.active7}</strong>{' '}
                          <span className="text-muted-foreground">
                            active this week
                          </span>
                        </span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-1">
                      <span>{c.user_count} users</span>
                      {c.disabled_count > 0 && (
                        <span>{c.disabled_count} disabled</span>
                      )}
                      <span>{c.flag_count} features on</span>
                      <span>{c.entitlement_count} entitlements</span>
                    </div>
                  </Card>
                  );
                })}
                {customers.length === 0 && (
                  <Card className="p-6 text-sm text-muted-foreground">
                    No customers are visible to this account.
                  </Card>
                )}
              </div>
              )
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setParams({});
                      // The counts on the cards may have changed here.
                      apiAdminCustomers()
                        .then((r) => setCustomers(r.items))
                        .catch(() => undefined);
                      setCompanyStats(null);
                    }}
                  >
                    <ChevronLeft className="size-4 mr-1" /> All customers
                  </Button>
                  <span className="font-semibold">{current?.name}</span>
                  {busy && (
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  )}
                </div>

                {usage && (
                  <Card className="p-4">
                    <div className="text-xs text-muted-foreground mb-2">
                      Last {usage.days} days
                    </div>
                    <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                      {/* Answers the model wrote (chat_llm_usage 'tool'),
                          which can differ a little from the questions typed
                          that Usage counts: retries, skills, ask-choice. */}
                      <span title="Answers the model wrote in this period">
                        <strong>{usage.questions}</strong> answers
                      </span>
                      <span>
                        <strong>{usage.sessions}</strong> sessions
                      </span>
                      <span>
                        cache hit{' '}
                        <strong>{Math.round(usage.cache_ratio * 100)}%</strong>
                      </span>
                      <span>
                        avg <strong>{(usage.avg_ms / 1000).toFixed(1)}s</strong>
                      </span>
                      <span>
                        <strong>{usage.reports_sent}</strong> reports emailed
                      </span>
                      {usage.total_cost_usd !== undefined && (
                        <span className="text-muted-foreground">
                          our cost{' '}
                          <strong>${usage.total_cost_usd.toFixed(2)}</strong>
                        </span>
                      )}
                      {usage.openrouter_cost_usd !== undefined && (
                        <span className="text-muted-foreground">
                          OpenRouter{' '}
                          <strong>${usage.openrouter_cost_usd.toFixed(2)}</strong>
                        </span>
                      )}
                      {usage.anthropic_cost_usd !== undefined && (
                        <span className="text-muted-foreground">
                          Anthropic{' '}
                          <strong>${usage.anthropic_cost_usd.toFixed(2)}</strong>
                        </span>
                      )}
                    </div>
                  </Card>
                )}

                <div className="flex gap-1 border-b overflow-x-auto">
                  {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap ${
                          tab === t.id
                            ? 'border-primary text-foreground font-medium'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Icon className="size-4" />
                        {t.label}
                        {t.id === 'reports' && !!reports?.attention && (
                          <span className="ml-1 rounded-full bg-red-600 text-white text-[10px] leading-none px-1.5 py-0.5">
                            {reports.attention}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {tab === 'features' && detail && (
                  <div className="flex flex-col gap-4">
                    <Card className="p-4">
                      <div className="font-medium mb-1">
                        Plan (what this customer bought)
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        {me.role === 'SUPER_ADMIN'
                          ? 'Entitlements are the ceiling a partner admin can allocate within.'
                          : 'Set by BuduCloud. You can assign these to your users, up to the purchased seats.'}
                      </p>
                      <div className="flex flex-col gap-2">
                        {me.grantable_features.map((feature) => {
                          const ent = entitlementFor(feature);
                          const used = seatsUsed(feature);
                          return (
                            <div
                              key={feature}
                              className="flex items-center justify-between gap-3 flex-wrap border rounded-lg px-3 py-2"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium">
                                  {featureLabel(feature)}
                                </div>
                                {featureHelp(feature) && (
                                  <div className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                                    {featureHelp(feature)}
                                  </div>
                                )}
                                <div className="text-xs text-muted-foreground mt-1">
                                  {ent
                                    ? `${ent.seats === null ? 'unlimited' : `${used} / ${ent.seats}`} seats used${
                                        ent.expires_at
                                          ? ` · expires ${ent.expires_at}`
                                          : ''
                                      }`
                                    : 'not purchased'}
                                  {tenantFlags.get(feature) &&
                                    ' · on for all users'}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {me.role === 'SUPER_ADMIN' && (
                                  <>
                                    <Input
                                      key={`seats-${feature}-${ent?.seats ?? ''}`}
                                      className="h-8 w-24"
                                      placeholder="seats"
                                      defaultValue={ent?.seats ?? ''}
                                      onBlur={(e) => {
                                        const raw = e.target.value.trim();
                                        if (raw === '' && !ent) return;
                                        act(
                                          () =>
                                            apiAdminSetEntitlement(selected, {
                                              feature,
                                              seats:
                                                raw === '' ? null : Number(raw),
                                              expires_at:
                                                ent?.expires_at ?? null
                                            }),
                                          `${featureLabel(feature)} entitlement saved.`
                                        );
                                      }}
                                    />
                                    {ent && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-600"
                                        disabled={busy}
                                        onClick={() =>
                                          window.confirm(
                                            `Remove ${featureLabel(feature)} from this customer's plan? Users holding it keep it until you switch it off.`
                                          ) &&
                                          act(
                                            () =>
                                              apiAdminDeleteEntitlement(
                                                selected,
                                                feature
                                              ),
                                            `${featureLabel(feature)} entitlement removed.`
                                          )
                                        }
                                      >
                                        Remove
                                      </Button>
                                    )}
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground pl-2">
                                      all users
                                      <Switch
                                        checked={!!tenantFlags.get(feature)}
                                        disabled={busy}
                                        onCheckedChange={(v) =>
                                          act(
                                            () =>
                                              apiAdminSetFlag({
                                                customer_pk: selected,
                                                feature,
                                                enabled: v,
                                                user_id: null
                                              }),
                                            `${featureLabel(feature)} ${v ? 'enabled' : 'disabled'} for all users.`
                                          )
                                        }
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card>

                    <Card className="p-4">
                      <div className="font-medium mb-1">
                        Extra prompt instructions
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        Appended to this customer's system prompt on every
                        question.
                      </p>
                      <Textarea
                        rows={4}
                        dir="auto"
                        value={promptDraft}
                        onChange={(e) => setPromptDraft(e.target.value)}
                      />
                      <div className="flex justify-end mt-2">
                        <Button
                          size="sm"
                          disabled={
                            busy ||
                            promptDraft ===
                              (detail.customer.additional_prompt ?? '')
                          }
                          onClick={() =>
                            act(
                              () =>
                                apiAdminPatchCustomer(selected, {
                                  additional_prompt: promptDraft
                                }),
                              'Prompt instructions saved.'
                            )
                          }
                        >
                          Save
                        </Button>
                      </div>
                    </Card>

                    {me.role === 'SUPER_ADMIN' && (
                      <Card className="p-4">
                        <div className="font-medium mb-3">
                          Ceilings a partner cannot exceed
                        </div>
                        <div className="flex flex-wrap gap-4">
                          <div className="grid gap-1">
                            <Label className="text-xs">
                              Messages / user / month
                            </Label>
                            <Input
                              key={`mml-${detail.customer.max_message_limit ?? ''}`}
                              className="h-8 w-32"
                              defaultValue={
                                detail.customer.max_message_limit ?? ''
                              }
                              onBlur={(e) => {
                                const raw = e.target.value.trim();
                                if (
                                  raw === '' ||
                                  Number(raw) ===
                                    detail.customer.max_message_limit
                                )
                                  return;
                                act(
                                  () =>
                                    apiAdminPatchCustomer(selected, {
                                      max_message_limit: Number(raw)
                                    }),
                                  'Message ceiling saved.'
                                );
                              }}
                            />
                          </div>
                          <div className="grid gap-1">
                            <Label className="text-xs">Max users</Label>
                            <Input
                              key={`mu-${detail.customer.max_users ?? ''}`}
                              className="h-8 w-32"
                              defaultValue={detail.customer.max_users ?? ''}
                              onBlur={(e) => {
                                const raw = e.target.value.trim();
                                if (
                                  raw === '' ||
                                  Number(raw) === detail.customer.max_users
                                )
                                  return;
                                act(
                                  () =>
                                    apiAdminPatchCustomer(selected, {
                                      max_users: Number(raw)
                                    }),
                                  'User ceiling saved.'
                                );
                              }}
                            />
                          </div>
                          {detail.customer.database && (
                            <div className="grid gap-1">
                              <Label className="text-xs">Database</Label>
                              <div className="text-sm text-muted-foreground pt-1.5">
                                {detail.customer.database.database} @{' '}
                                {detail.customer.database.host}
                              </div>
                            </div>
                          )}
                        </div>
                      </Card>
                    )}
                  </div>
                )}

                {tab === 'users' && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-muted-foreground">
                        {users.length} user{users.length === 1 ? '' : 's'}
                        {current?.max_users != null
                          ? ` of ${current.max_users} allowed`
                          : ''}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCreating(true)}
                      >
                        <UserPlus className="size-4 mr-1" /> New user
                      </Button>
                    </div>
                    {users.map((u) => (
                      <Card
                        key={u.pk}
                        className={`p-4 ${u.disabled ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="font-medium flex items-center gap-2 flex-wrap">
                              {u.email}
                              {u.role !== 'USER' && (
                                <Badge
                                  variant="outline"
                                  className="text-[11px]"
                                >
                                  {u.role}
                                </Badge>
                              )}
                              {u.disabled && (
                                <Badge
                                  variant="outline"
                                  className="text-[11px]"
                                >
                                  disabled
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {u.monthly_message_count ?? 0} /{' '}
                              {u.base_message_limit ?? 0} messages this month
                              {u.last_day ? ` · last day ${u.last_day}` : ''}
                            </div>
                            {seenBy.get(u.id) && (
                              <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                                <span
                                  title={formatDateTime(
                                    seenBy.get(u.id)!.last_seen_at
                                  )}
                                >
                                  last seen{' '}
                                  {timeAgo(seenBy.get(u.id)!.last_seen_at)}
                                </span>
                                {seenBy.get(u.id)!.last_client === 'mobile' && (
                                  <Smartphone className="size-3" />
                                )}
                                {seenBy.get(u.id)!.last_client === 'web' && (
                                  <Globe className="size-3" />
                                )}
                                <span>
                                  · {seenBy.get(u.id)!.questions} questions in
                                  30 days ({seenBy.get(u.id)!.questions_total}{' '}
                                  all time)
                                </span>
                                {seenBy.get(u.id)!.questions_total > 0 && (
                                  <button
                                    className="text-primary hover:underline"
                                    onClick={() =>
                                      setParams({
                                        customer: String(selected),
                                        tab: 'conversations',
                                        user: u.id
                                      })
                                    }
                                  >
                                    conversations
                                  </button>
                                )}
                              </div>
                            )}
                            {u.role === 'USER' && (
                              <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
                                <span
                                  className={
                                    u.welcome?.status === 'FAILED'
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-muted-foreground'
                                  }
                                >
                                  {!u.welcome
                                    ? 'No welcome email yet'
                                    : u.welcome.status === 'SENT'
                                      ? `Welcome email sent ${u.welcome.at.slice(0, 10)} to ${u.welcome.send_to}`
                                      : u.welcome.status === 'FAILED'
                                        ? `Welcome email failed ${u.welcome.at.slice(0, 10)}`
                                        : `Welcome email unconfirmed since ${u.welcome.at.slice(0, 10)}`}
                                </span>
                                <button
                                  className="text-primary hover:underline disabled:opacity-50"
                                  disabled={busy || u.disabled}
                                  onClick={() => setWelcomeFor(u)}
                                >
                                  Send welcome email
                                </button>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {me.grantable_features.map((feature) => {
                                const on = u.user_flags.includes(feature);
                                return (
                                  <button
                                    key={feature}
                                    disabled={busy}
                                    onClick={() =>
                                      act(
                                        () =>
                                          apiAdminSetFlag({
                                            customer_pk: selected,
                                            feature,
                                            enabled: !on,
                                            user_id: u.id
                                          }),
                                        `${featureLabel(feature)} ${on ? 'removed from' : 'granted to'} ${u.email}.`
                                      )
                                    }
                                    title={
                                      featureHelp(feature) ??
                                      featureLabel(feature)
                                    }
                                    className={`text-[11px] rounded-full border px-2 py-0.5 transition-colors ${
                                      on
                                        ? 'border-primary bg-primary/10 text-foreground'
                                        : 'text-muted-foreground hover:bg-muted'
                                    }`}
                                  >
                                    {featureLabel(feature)}
                                  </button>
                                );
                              })}
                              {u.role === 'USER' && (
                                <button
                                  disabled={busy}
                                  onClick={() => {
                                    const on =
                                      u.user_flags.includes(ENGLISH_ONLY);
                                    act(
                                      () =>
                                        apiAdminPatchUser(u.pk, {
                                          english_only: !on
                                        }),
                                      `English only ${on ? 'switched off' : 'switched on'} for ${u.email}.`
                                    );
                                  }}
                                  title={featureHelp(ENGLISH_ONLY) ?? ''}
                                  className={`text-[11px] rounded-full border px-2 py-0.5 transition-colors ${
                                    u.user_flags.includes(ENGLISH_ONLY)
                                      ? 'border-primary bg-primary/10 text-foreground'
                                      : 'text-muted-foreground hover:bg-muted'
                                  }`}
                                >
                                  {featureLabel(ENGLISH_ONLY)}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex items-end gap-3 flex-wrap">
                            <div className="grid gap-1">
                              <Label className="text-xs">Msg limit</Label>
                              <Input
                                key={`bml-${u.pk}-${u.base_message_limit ?? ''}`}
                                className="h-8 w-24"
                                defaultValue={u.base_message_limit ?? ''}
                                onBlur={(e) => {
                                  const raw = e.target.value.trim();
                                  if (
                                    raw === '' ||
                                    Number(raw) === u.base_message_limit
                                  )
                                    return;
                                  act(
                                    () =>
                                      apiAdminPatchUser(u.pk, {
                                        base_message_limit: Number(raw)
                                      }),
                                    `Limit updated for ${u.email}.`
                                  );
                                }}
                              />
                            </div>
                            <div className="grid gap-1">
                              {/* The last day the user can work, as on New
                                  user. It used to show the stored first
                                  blocked day as a timestamp, which a date
                                  field cannot display: it looked empty. */}
                              <Label className="text-xs">Last day</Label>
                              <Input
                                key={`ld-${u.pk}-${u.last_day ?? ''}`}
                                type="date"
                                className="h-8 w-36"
                                defaultValue={u.last_day ?? ''}
                                onBlur={(e) => {
                                  const raw = e.target.value;
                                  if (raw === (u.last_day ?? '')) return;
                                  if (
                                    raw === '' &&
                                    !window.confirm(
                                      `Remove the end date for ${u.email}? They will keep access with no last day.`
                                    )
                                  ) {
                                    e.target.value = u.last_day ?? '';
                                    return;
                                  }
                                  act(
                                    () =>
                                      apiAdminPatchUser(u.pk, {
                                        last_day: raw
                                      }),
                                    raw
                                      ? `${u.email} can work until ${raw}.`
                                      : `End date removed for ${u.email}.`
                                  );
                                }}
                              />
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground pb-1.5">
                              active
                              <Switch
                                checked={!u.disabled}
                                disabled={busy}
                                onCheckedChange={(v) =>
                                  act(
                                    () =>
                                      apiAdminPatchUser(u.pk, { disabled: !v }),
                                    `${u.email} ${v ? 'enabled' : 'disabled'}.`
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                    {users.length === 0 && (
                      <Card className="p-6 text-sm text-muted-foreground">
                        No users.
                      </Card>
                    )}
                  </div>
                )}

                {tab === 'data' && data && (
                  <div className="flex flex-col gap-4">
                    <Card className="p-4">
                      <div className="font-medium mb-1">
                        What this company has taught Signals
                        {memory?.length ? ` (${memory.length})` : ''}
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        Facts someone here confirmed that the data cannot say on
                        its own — a branch that closed, a line that was dropped.
                        Every one of these changes what gets counted for
                        everyone in the company, which is why they are listed
                        here with the person who confirmed them and can be
                        removed by any admin.
                      </p>
                      {memory === null && (
                        <div className="text-xs text-muted-foreground">
                          Loading…
                        </div>
                      )}
                      {memory?.length === 0 && (
                        <div className="text-xs text-muted-foreground">
                          Nothing remembered yet.
                        </div>
                      )}
                      {proposals.length > 0 && (
                        <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
                          <div className="text-xs font-medium mb-2">
                            Waiting for approval ({proposals.length})
                          </div>
                          <div className="flex flex-col gap-2">
                            {proposals.map((p) => (
                              <div
                                key={p.id}
                                className="flex items-start justify-between gap-3 flex-wrap"
                              >
                                <div className="min-w-0">
                                  <div className="text-sm">{p.fact}</div>
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    suggested by {p.proposed_by_email || 'unknown'} ·{' '}
                                    {new Date(p.created_at).toLocaleDateString()}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  {(['approve', 'reject'] as const).map((d) => (
                                    <Button
                                      key={d}
                                      size="sm"
                                      variant={d === 'approve' ? 'default' : 'outline'}
                                      disabled={busy}
                                      onClick={() =>
                                        act(
                                          () =>
                                            apiAdminReviewMemory(selected!, p.id, d),
                                          d === 'approve'
                                            ? 'Approved: it now applies to everyone in the company.'
                                            : 'Rejected.'
                                        )
                                      }
                                    >
                                      {d === 'approve' ? 'Approve' : 'Reject'}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col gap-2">
                        {(memory ?? []).map((m) => (
                          <div
                            key={m.id}
                            className={cn(
                              'border rounded-lg p-3 flex items-start justify-between gap-3',
                              m.contradicted_at && 'opacity-60 border-amber-300'
                            )}
                          >
                            <div className="min-w-0">
                              <div className="text-sm">{m.fact}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {m.created_by_email || 'unknown'} ·{' '}
                                {new Date(m.created_at).toLocaleDateString()}
                                {m.check_kind
                                  ? ' · checked against the data nightly'
                                  : ' · cannot be checked automatically'}
                              </div>
                              {m.contradicted_at && (
                                <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                                  The data disagrees with this — rows for it
                                  appeared again, so it stopped being applied on{' '}
                                  {new Date(
                                    m.contradicted_at
                                  ).toLocaleDateString()}
                                  .
                                </div>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                if (selected === null) return;
                                if (
                                  !window.confirm(
                                    'Remove this fact? It stops applying to everyone in the company.'
                                  )
                                )
                                  return;
                                try {
                                  await apiAdminDeleteMemory(selected, m.id);
                                  setMemory((rows) =>
                                    (rows ?? []).filter((r) => r.id !== m.id)
                                  );
                                  setNotice({
                                    kind: 'ok',
                                    text: 'Memory removed.'
                                  });
                                } catch (e: any) {
                                  setNotice({ kind: 'err', text: e.message });
                                }
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    </Card>

                    <Card className="p-4">
                      <div className="font-medium mb-1">
                        Table descriptions ({data.described} / {data.total}{' '}
                        described)
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        This text goes into every system prompt. A table with no
                        description leaves the model guessing what it means from
                        three sample rows — so say what the numbers ARE: net
                        sales after discounts, returns negative, which table is
                        the official summary.
                      </p>
                      {!data.dfs_available && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200 mb-3">
                          This customer has no data catalogue yet, so Signals
                          loads a default table list and gets no business
                          descriptions. Creating one changes which tables the
                          model sees, so BuduCloud sets it up deliberately.
                        </div>
                      )}
                      {data.dfs_available && !data.supports_descriptions && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200 mb-3">
                          This customer's catalogue is on an older schema that
                          cannot store descriptions yet — BuduCloud has to
                          upgrade it first.
                        </div>
                      )}
                      <div className="flex flex-col gap-3">
                        {data.tables.map((t) => (
                          <div
                            key={t.table_name}
                            className="border rounded-lg p-3"
                          >
                            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                              <span className="font-mono text-xs">
                                {t.table_name}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {t.missing_table
                                  ? 'described but the table no longer exists'
                                  : `~${(t.approx_rows ?? 0).toLocaleString()} rows`}
                                {!t.description && ' · no description'}
                              </span>
                            </div>
                            <Textarea
                              rows={2}
                              dir="auto"
                              disabled={!data.supports_descriptions}
                              defaultValue={t.description ?? ''}
                              placeholder="e.g. Sales invoices — one row per posted invoice line; NET_AMOUNT is after discounts and returns are negative."
                              onBlur={(e) => {
                                const next = e.target.value;
                                if (next === (t.description ?? '')) return;
                                act(
                                  () =>
                                    apiAdminSetDescription(selected, {
                                      table_name: t.table_name,
                                      description: next
                                    }),
                                  `${t.table_name} description saved.`
                                );
                              }}
                            />
                          </div>
                        ))}
                        {data.tables.length === 0 && (
                          <p className="text-sm text-muted-foreground">
                            No data tables found in this customer&apos;s
                            database.
                          </p>
                        )}
                      </div>
                    </Card>

                    {visibility && (
                      <Card className="p-4">
                        <div className="font-medium mb-1">
                          Which topics Signals Chat may use
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">
                          Two different switches. <strong>ETL enabled</strong>{' '}
                          means the nightly job keeps the topic up to date.{' '}
                          <strong>Available in chat</strong> means a
                          conversation may load it and the model may see it.
                          Turning chat availability off keeps the ETL and the
                          stored data running, but removes this topic from
                          Signals conversations — and no user permission can
                          bring it back while it is off. The change reaches a
                          chat that is already open before its next question;
                          an answer already being written finishes.
                        </p>

                        {visibility.tables.some((t) => !t.sandbox_supported) && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200 mb-3">
                            This customer&apos;s database predates the chat
                            availability flag, so the two cannot be told apart
                            here yet. BuduCloud has to run the migration on it
                            first.
                          </div>
                        )}

                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-muted-foreground">
                                <th className="py-1 pr-3 font-medium">Topic</th>
                                <th className="py-1 pr-3 font-medium">ETL enabled</th>
                                <th className="py-1 pr-3 font-medium">Available in chat</th>
                                <th className="py-1 font-medium">In the database</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibility.tables.map((t) => (
                                <tr key={t.df_id} className="border-t">
                                  <td className="py-2 pr-3 align-top">
                                    <span className="font-mono">{t.name}</span>
                                    {t.description && (
                                      <div className="text-xs text-muted-foreground max-w-md">
                                        {t.description.slice(0, 110)}
                                      </div>
                                    )}
                                  </td>
                                  <td className="py-2 pr-3 align-top">
                                    {t.enabled ? (
                                      <span className="text-emerald-700 dark:text-emerald-400">
                                        loading nightly
                                      </span>
                                    ) : (
                                      <span className="text-muted-foreground">
                                        not loading
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-2 pr-3 align-top">
                                    <label className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        className="size-4"
                                        checked={t.sandbox_enabled}
                                        disabled={
                                          !t.sandbox_supported || savingTopic === t.name
                                        }
                                        onChange={async (e) => {
                                          const available = e.target.checked;
                                          setSavingTopic(t.name);
                                          try {
                                            await apiAdminSetChatAvailability(
                                              selected,
                                              { table_name: t.name, available }
                                            );
                                            // Re-read rather than patching the
                                            // row in place: the server decides
                                            // what "usable" now means, and the
                                            // audit entry is the receipt.
                                            setVisibility(
                                              await apiAdminVisibility(selected)
                                            );
                                            setAudit((await apiAdminAudit(selected)).items);
                                          } catch (err: any) {
                                            setNotice({
                                              kind: 'err',
                                              text: err.message
                                            });
                                          } finally {
                                            setSavingTopic(null);
                                          }
                                        }}
                                      />
                                      <span>
                                        {t.sandbox_enabled ? 'in chat' : 'withheld'}
                                      </span>
                                    </label>
                                  </td>
                                  <td className="py-2 align-top">
                                    {t.present ? (
                                      'yes'
                                    ) : (
                                      <span className="text-amber-700 dark:text-amber-400">
                                        missing
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    )}

                    {visibility && (
                      <Card className="p-4">
                        <div className="font-medium mb-1">
                          Who can see which tables
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">
                          By default every user of this customer can query the
                          tenant&apos;s whole catalogue. Switch a person to a
                          hand-picked list and they get those tables and nothing
                          else — including nothing at all, if you tick none.
                          The change reaches a chat that is already open before
                          its next question; an answer already being written
                          finishes.
                        </p>

                        {!visibility.custom_supported && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200 mb-3">
                            This customer&apos;s database does not have the
                            table-access schema yet, so a hand-picked list
                            cannot be switched on here. BuduCloud has to run the
                            migration on it first.
                          </div>
                        )}

                        <div className="flex flex-col gap-4">
                          {users.map((u) => {
                            const isCustom = visibility.custom.includes(u.id);
                            const granted = visibility.grants[u.id] ?? [];
                            return (
                              <div
                                key={u.id}
                                className="border rounded-lg p-3 flex flex-col gap-2"
                              >
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <span className="text-sm">{u.email}</span>
                                  <label className="flex items-center gap-2 text-xs">
                                    <input
                                      type="checkbox"
                                      checked={isCustom}
                                      disabled={!visibility.custom_supported}
                                      onChange={async (e) => {
                                        if (selected === null) return;
                                        const custom = e.target.checked;
                                        try {
                                          await apiAdminSetTableAccessMode(
                                            selected,
                                            { user_id: u.id, custom }
                                          );
                                          setVisibility((v) =>
                                            v
                                              ? {
                                                  ...v,
                                                  custom: custom
                                                    ? [...v.custom, u.id]
                                                    : v.custom.filter(
                                                        (x) => x !== u.id
                                                      )
                                                }
                                              : v
                                          );
                                          setNotice({
                                            kind: 'ok',
                                            text: custom
                                              ? `${u.email} is now on a hand-picked list.`
                                              : `${u.email} is back on the default tables.`
                                          });
                                        } catch (err: any) {
                                          setNotice({
                                            kind: 'err',
                                            text: err.message
                                          });
                                        }
                                      }}
                                    />
                                    Use a hand-picked table list
                                  </label>
                                </div>

                                {isCustom ? (
                                  <div className="flex flex-col gap-1">
                                    {visibility.tables.map((t) => (
                                      <label
                                        key={t.df_id}
                                        className="flex items-start gap-2 text-xs"
                                      >
                                        <input
                                          type="checkbox"
                                          className="mt-0.5"
                                          checked={granted.includes(t.df_id)}
                                          onChange={async (e) => {
                                            if (selected === null) return;
                                            const visible = e.target.checked;
                                            try {
                                              await apiAdminSetVisibility(
                                                selected,
                                                {
                                                  user_id: u.id,
                                                  df_id: t.df_id,
                                                  visible
                                                }
                                              );
                                              setVisibility((v) => {
                                                if (!v) return v;
                                                const now = new Set(
                                                  v.grants[u.id] ?? []
                                                );
                                                if (visible) now.add(t.df_id);
                                                else now.delete(t.df_id);
                                                return {
                                                  ...v,
                                                  grants: {
                                                    ...v.grants,
                                                    [u.id]: [...now]
                                                  }
                                                };
                                              });
                                            } catch (err: any) {
                                              setNotice({
                                                kind: 'err',
                                                text: err.message
                                              });
                                            }
                                          }}
                                        />
                                        <span>
                                          <span className="font-mono">
                                            {t.name}
                                          </span>
                                          {!t.usable && (
                                            <span className="text-amber-700 dark:text-amber-400">
                                              {' '}
                                              —{' '}
                                              {!t.present
                                                ? 'not in the database'
                                                : !t.registered
                                                  ? 'not in the nightly sync'
                                                  : 'switched off in the sync'}
                                              , so granting it grants nothing
                                            </span>
                                          )}
                                          {t.usable && t.description && (
                                            <span className="text-muted-foreground">
                                              {' '}
                                              — {t.description.slice(0, 90)}
                                            </span>
                                          )}
                                        </span>
                                      </label>
                                    ))}
                                    {granted.length === 0 && (
                                      <p className="text-xs text-amber-700 dark:text-amber-400">
                                        Nothing ticked: this user can query no
                                        tables at all, and the chat will say so.
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    Inherits the tenant default
                                    {granted.length > 0
                                      ? ` (a list of ${granted.length} is saved and will come back if you tick the box)`
                                      : ''}
                                    .
                                  </p>
                                )}
                              </div>
                            );
                          })}
                          {users.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                              This customer has no users yet.
                            </p>
                          )}
                        </div>
                      </Card>
                    )}

                    {me.role === 'SUPER_ADMIN' && data.etl.length > 0 && (
                      <Card className="p-4">
                        <div className="font-medium mb-1">
                          Nightly data sync
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">
                          Read-only here. Endpoints and their credentials stay
                          in the ETL configuration.
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-muted-foreground text-left">
                                <th className="pb-2 pr-3">Table</th>
                                <th className="pb-2 pr-3">Enabled</th>
                                <th className="pb-2 pr-3">Last update</th>
                                <th className="pb-2 pr-3">Resync days</th>
                                <th className="pb-2">Date field</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.etl.map((e) => (
                                <tr key={e.table_name} className="border-t">
                                  <td className="py-2 pr-3 font-mono text-xs">
                                    {e.table_name}
                                  </td>
                                  <td className="py-2 pr-3">
                                    {e.enabled ? 'yes' : 'no'}
                                  </td>
                                  <td className="py-2 pr-3">
                                    {e.last_update_date ?? '—'}
                                  </td>
                                  <td className="py-2 pr-3">
                                    {e.resync_days ?? '—'}
                                  </td>
                                  <td className="py-2">
                                    {e.date_field ?? '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    )}

                    {me.role === 'SUPER_ADMIN' &&
                      data.sync_audit.length > 0 && (
                        <Card className="p-4">
                          <div className="font-medium mb-1">
                            Sync reconciliation
                          </div>
                          <p className="text-xs text-muted-foreground mb-3">
                            Rows the ERP API returned versus rows actually
                            stored. A mismatch means the customer would see
                            numbers that differ from their own ERP reports.
                          </p>
                          <div className="flex flex-col gap-1">
                            {data.sync_audit.map((row, i) => (
                              <div
                                key={i}
                                className="flex items-center justify-between gap-3 text-sm flex-wrap"
                              >
                                <span className="font-mono text-xs">
                                  {row.table_name}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {row.window_start} → {row.window_end}
                                </span>
                                <span className="text-xs">
                                  API {row.api_count} / DB {row.db_count}
                                </span>
                                <Badge
                                  variant="outline"
                                  className={`text-[11px] ${
                                    row.matched
                                      ? 'border-emerald-200 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300'
                                      : 'border-red-200 text-red-700 dark:border-red-800 dark:text-red-300'
                                  }`}
                                >
                                  {row.matched ? 'matched' : 'mismatch'}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </Card>
                      )}
                  </div>
                )}

                {tab === 'conversations' && selected !== null && (
                  <ConversationBrowser
                    key={`c-${selected}-${params.get('user') ?? ''}`}
                    customerPk={selected}
                    customers={customers}
                    users={users}
                    initialQuery={urlQuery}
                    initialUserId={params.get('user') ?? ''}
                    initialThread={
                      threadParam
                        ? { id: threadParam, customer_pk: selected }
                        : null
                    }
                    onStateChange={syncThread}
                    onError={showError}
                  />
                )}

                {tab === 'reports' && (
                  <div className="flex flex-col gap-3">
                    <p className="text-xs text-muted-foreground">
                      Every scheduled email report this company has, who made
                      it and what happened to its last sends. A failed send is
                      a customer who did not get the email they asked for.
                    </p>
                    {reports === null && !busy && (
                      <Card className="p-6 text-sm text-muted-foreground">
                        Could not load this company&apos;s reports.
                      </Card>
                    )}
                    {reports?.items.length === 0 && (
                      <Card className="p-6 text-sm text-muted-foreground">
                        This company has no scheduled reports.
                      </Card>
                    )}
                    {reports?.items.map((r) => (
                      <Card
                        key={r.id}
                        className={cn(
                          'p-4 flex flex-col gap-2',
                          r.attention && 'border-red-500/60'
                        )}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="font-medium" dir="auto">
                              {r.title}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {describeSchedule(r)} · made by{' '}
                              {r.owner_email ?? 'unknown'} · sent to{' '}
                              {r.recipient_email}
                            </div>
                          </div>
                          <div className="flex gap-1.5 flex-wrap">
                            {!r.active && <Badge variant="secondary">Off</Badge>}
                            {r.last_status === 'SENT' && !r.attention && (
                              <Badge variant="outline">Last send OK</Badge>
                            )}
                            {r.attention && (
                              <Badge variant="destructive">Needs attention</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground" dir="auto">
                          {r.question}
                        </div>
                        {r.attention && r.why && (
                          <div className="text-sm rounded-md bg-red-500/10 text-red-700 dark:text-red-300 px-3 py-2">
                            {r.why}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          Last run {formatDateTime(r.last_run_at)} · next run{' '}
                          {r.active ? formatDateTime(r.next_run_at) : '—'}
                        </div>
                        {r.sends.length > 0 && (
                          <div className="flex flex-col divide-y border rounded-md">
                            {r.sends.map((s) => (
                              <div
                                key={s.id}
                                className="flex items-start justify-between gap-3 px-3 py-2 text-xs"
                              >
                                <div className="flex items-start gap-2 min-w-0">
                                  <span
                                    className={cn(
                                      'font-medium whitespace-nowrap',
                                      s.status === 'SENT'
                                        ? 'text-green-700 dark:text-green-400'
                                        : 'text-red-700 dark:text-red-400'
                                    )}
                                  >
                                    {s.status === 'SENT'
                                      ? 'Sent'
                                      : s.status === 'EMPTY'
                                      ? 'Nothing sent'
                                      : 'Failed'}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {s.why ??
                                      `to ${s.to_email ?? r.recipient_email}`}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 whitespace-nowrap">
                                  {s.llm_cost_usd != null && (
                                    <span className="text-muted-foreground">
                                      ${Number(s.llm_cost_usd).toFixed(3)}
                                    </span>
                                  )}
                                  <span className="text-muted-foreground">
                                    {formatDateTime(s.sent_at)}
                                  </span>
                                  {s.has_email && selected !== null && (
                                    <button
                                      className="underline"
                                      onClick={() =>
                                        setEmailPreview(
                                          reportEmailUrl(selected, s.id)
                                        )
                                      }
                                    >
                                      View email
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card>
                    ))}
                    <Dialog
                      open={emailPreview !== null}
                      onOpenChange={(open) => !open && setEmailPreview(null)}
                    >
                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle>The email as it was sent</DialogTitle>
                        </DialogHeader>
                        {emailPreview && (
                          <iframe
                            src={emailPreview}
                            title="Sent report email"
                            sandbox=""
                            className="w-full h-[70vh] rounded border bg-white"
                          />
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>
                )}

                {tab === 'audit' && (
                  <div className="flex flex-col gap-2">
                    <p className="text-xs text-muted-foreground">
                      Every configuration change, in order. This is the record
                      of who granted what — read it before trusting any claim
                      about a customer&apos;s plan.
                    </p>
                    {audit.map((a) => {
                      const s = describeAudit(a, users);
                      return (
                        <Card key={a.id} className="p-3">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="text-sm flex items-start gap-2 min-w-0">
                              <span className="text-base leading-none mt-0.5">
                                {s.icon}
                              </span>
                              <span>{s.text}</span>
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDateTime(a.created_at)}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1.5 pl-6">
                            by {a.actor_email}
                            {a.actor_role === 'PARTNER_ADMIN'
                              ? ' (partner)'
                              : ''}
                          </div>
                          {s.detail && (
                            <div className="text-xs text-muted-foreground mt-1 pl-6">
                              {s.detail}
                            </div>
                          )}
                        </Card>
                      );
                    })}
                    {audit.length === 0 && (
                      <Card className="p-6 text-sm text-muted-foreground">
                        No configuration changes recorded for this customer yet.
                      </Card>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
